import { useEffect, useState } from "react";
import { Paiement, PaiementInfo } from "../../model/Model";
import { ajouterPaiement, getPaiements, modifierPaiement, supprimerPaiement, telechargerRecuPaiement } from "../../service/paiements/paiements.service";
import { AjouterPaiementPopup } from "./AjouterPaiement";
import PaiementTemplates from "./PaiementTemplates";

type TabType = 'paiements' | 'templates';

function Paiements(){
    const [paiements, setPaiements] = useState<PaiementInfo[]>([]);
    const [seen, setSeen] = useState(false);
    const [paiementId, setPaiementId] = useState<number | undefined>(undefined);
    const [menuOpen, setMenuOpen] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>('paiements');
    const [downloadingId, setDownloadingId] = useState<number | null>(null);

    useEffect(() => {
        async function fetchData() {
            const data = await getPaiements();
            console.log(data);
            setPaiements(data);
        }
        fetchData();
    }, []);

    // Fermer le menu quand on clique en dehors
    useEffect(() => {
        const handleClickOutside = () => {
            if (menuOpen !== null) {
                setMenuOpen(null);
            }
        };
        
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [menuOpen]);

    function togglePopup() {
        setSeen(!seen);
        setPaiementId(undefined);
    }

    const closePopup = () => {
        setSeen(false);
        setPaiementId(undefined);
    };

    async function ajouterModifier(paiement: Paiement): Promise<void> {
        try {
            if (paiement.paiementId) {
                await modifierPaiement(paiement);
            } else {
                await ajouterPaiement(paiement);
            }
        } catch (error) {
            console.log(error);
        } finally {
            const paiements = await getPaiements();
            setPaiements(paiements);
            setSeen(false);
        }
    }

    async function handleSupprimer(id: number | undefined) {
        if (id === undefined) return;
        try {
            await supprimerPaiement(id);
            const paiements = await getPaiements();
            setPaiements(paiements);
        } catch (error) {
            console.log(error);
        }
    }

    function handleModifier(id: number | undefined) {
        setPaiementId(id);
        setSeen(true);
    }

    const handleTelechargerRecu = async (id: number | undefined) => {
        if (!id) {
            alert('ID de paiement invalide');
            return;
        }
        
        try {
            setMenuOpen(null);
            setDownloadingId(id);
            await telechargerRecuPaiement(id);
        } catch (error) {
            console.error('Erreur lors du téléchargement:', error);
            alert('Erreur lors du téléchargement du reçu. Veuillez réessayer.');
        } finally {
            setDownloadingId(null);
        }
    };

    const toggleMenu = (id: number | undefined) => {
        if (id !== undefined) {
            setMenuOpen(menuOpen === id ? null : id);
        }
    };

    const handleMenuClick = (e: React.MouseEvent, id: number | undefined) => {
        e.stopPropagation();
        toggleMenu(id);
    };

    const getModeDePaiementBadge = (mode: string) => {
        const baseClasses = "inline-flex px-2 py-1 text-xs font-semibold rounded-full";
        switch (mode) {
            case 'VIREMENT':
                return `${baseClasses} bg-blue-100 text-blue-800`;
            case 'ESPECE':
                return `${baseClasses} bg-green-100 text-green-800`;
            case 'CHEQUE':
                return `${baseClasses} bg-purple-100 text-purple-800`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800`;
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'Non définie';
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    };

    return(
        <div className="pt-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Navigation par onglets */}
                <div className="mb-6 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('paiements')}
                            className={`${
                                activeTab === 'paiements'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 cursor-pointer`}
                        >
                            <div className="flex items-center space-x-2">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                <span>Liste des paiements</span>
                                {paiements.length > 0 && (
                                    <span className="bg-blue-100 text-blue-600 py-0.5 px-2 rounded-full text-xs font-medium">
                                        {paiements.length}
                                    </span>
                                )}
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('templates')}
                            className={`${
                                activeTab === 'templates'
                                    ? 'border-green-500 text-green-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 cursor-pointer`}
                        >
                            <div className="flex items-center space-x-2">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span>Templates de paiements</span>
                            </div>
                        </button>
                    </nav>
                </div>

                {/* Contenu des onglets */}
                {activeTab === 'paiements' && (
                    <>
                <div className="mb-6">
                    <button 
                        onClick={togglePopup}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md"
                    >
                        + Enregistrer un paiement
                    </button>
                    {seen ? <AjouterPaiementPopup ajouterModifier={ajouterModifier} onClose={closePopup} paiementId={paiementId}/> : null}
                </div>

                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-800">Liste des paiements</h2>
                        <p className="text-sm text-gray-600 mt-1">Historique des loyers et charges</p>
                    </div>
                    
                    {paiements?.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date paiement
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Contrat
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Bien
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Locataire
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Période couverte
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Montant
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Mode
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paiements?.map((paiement, index) => (
                                        <tr key={paiement.paiementId || index} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {formatDate(paiement.dateDePaiement)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {paiement.contratIdentifiant || `#${paiement.contrat?.contratId}`}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                <div className="flex flex-col">
                                                    <span className="font-medium">
                                                        {paiement.bienAdresse || paiement.contrat?.bien?.adresse || <span className="text-gray-400 italic">Non renseigné</span>}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {paiement.contrat?.bien?.ville}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                {paiement.locataireNom || `${paiement.contrat?.locataire?.prenom} ${paiement.contrat?.locataire?.nom}` || <span className="text-gray-400 italic">Non renseigné</span>}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                <div className="flex flex-col">
                                                    <span className="text-xs text-gray-500">Du {formatDate(paiement.dateDeDebutEffet)}</span>
                                                    <span className="text-xs text-gray-500">Au {formatDate(paiement.dateDeFinEffet)}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                {formatCurrency(paiement.montant)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={getModeDePaiementBadge(paiement.modeDePaiement)}>
                                                    {paiement.modeDePaiement}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="relative inline-block text-left">
                                                    <button 
                                                        onClick={(e) => handleMenuClick(e, paiement.paiementId)}
                                                        className="text-gray-600 hover:text-gray-900 p-2 rounded-full hover:bg-gray-100 transition-colors"
                                                        title="Actions"
                                                    >
                                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                                                        </svg>
                                                    </button>
                                                    
                                                    {menuOpen === paiement.paiementId && (
                                                        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                                            <div className="py-1">
                                                                <button
                                                                    className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors flex items-center"
                                                                    onClick={() => handleTelechargerRecu(paiement.paiementId)}
                                                                    disabled={downloadingId === paiement.paiementId}
                                                                >
                                                                    {downloadingId === paiement.paiementId ? (
                                                                        <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                    ) : (
                                                                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                        </svg>
                                                                    )}
                                                                    {downloadingId === paiement.paiementId ? 'Téléchargement...' : 'Télécharger reçu'}
                                                                </button>
                                                                <button
                                                                    className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 transition-colors flex items-center"
                                                                    onClick={() => {
                                                                        setMenuOpen(null);
                                                                        handleModifier(paiement.paiementId);
                                                                    }}
                                                                >
                                                                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                    </svg>
                                                                    Modifier
                                                                </button>
                                                                <button
                                                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center"
                                                                    onClick={() => {
                                                                        setMenuOpen(null);
                                                                        handleSupprimer(paiement.paiementId);
                                                                    }}
                                                                >
                                                                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                    Supprimer
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="px-6 py-12 text-center">
                            <div className="text-gray-400 mb-4">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun paiement enregistré</h3>
                            <p className="text-gray-500">Cliquez sur "Enregistrer un paiement" pour commencer.</p>
                        </div>
                    )}
                </div>
                    </>
                )}

                {activeTab === 'templates' && (
                    <PaiementTemplates />
                )}
            </div>
        </div>
    )
}
export default Paiements;